import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime, money } from "../lib/format";
import { ui } from "../lib/ui";

interface MovimientoCaja {
  id: number;
  fecha: string;
  tipo: "INGRESO" | "EGRESO" | "GASTO" | "RETIRO";
  concepto: string;
  monto: number;
  medio_pago: string;
}

interface CajaActual {
  id: number;
  fecha_apertura: string;
  monto_inicial: number;
  ventas_total: number;
  ventas_cantidad: number;
  por_medio_pago: Record<string, number>;
  movimientos: MovimientoCaja[];
  ingresos_efectivo: number;
  egresos_efectivo: number;
  esperado_efectivo: number;
}

const DENOMS = [10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10];

const MEDIO_LABELS: Record<string, string> = {
  EFECTIVO: "Efectivo",
  DEBITO: "Tarjeta Débito",
  CREDITO: "Tarjeta Crédito",
  TARJETA: "Tarjeta",
  TRANSFERENCIA: "Transferencia",
  CTACTE: "Cta. Corriente",
  QR: "MP QR",
  POINT: "MP Point",
};
const MEDIO_COLORS: Record<string, string> = {
  EFECTIVO: "bg-emerald-50 border-emerald-200 text-emerald-700",
  DEBITO: "bg-blue-50 border-blue-200 text-blue-700",
  CREDITO: "bg-indigo-50 border-indigo-200 text-indigo-700",
  TARJETA: "bg-blue-50 border-blue-200 text-blue-700",
  TRANSFERENCIA: "bg-violet-50 border-violet-200 text-violet-700",
  CTACTE: "bg-amber-50 border-amber-200 text-amber-700",
  QR: "bg-sky-50 border-sky-200 text-sky-700",
  POINT: "bg-sky-50 border-sky-200 text-sky-700",
};

export default function Caja() {
  const qc = useQueryClient();
  const [montoInicial, setMontoInicial] = useState<number>(0);
  const [montoReal, setMontoReal] = useState<number>(0);
  const [obs, setObs] = useState("");
  const [arqueo, setArqueo] = useState<Record<number, number>>({});

  const { data: caja, isLoading } = useQuery({
    queryKey: ["caja-actual"],
    queryFn: async () => {
      const { data } = await api.get<CajaActual | null>("/api/cash/current");
      return data;
    },
    refetchInterval: 5000,
  });

  const abrir = useMutation({
    mutationFn: async () => {
      await api.post("/api/cash/open", null, {
        params: { monto_inicial: montoInicial },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["caja-actual"] });
      setMontoInicial(0);
    },
  });

  const cerrar = useMutation({
    mutationFn: async () => {
      const arqueoPayload = Object.fromEntries(
        Object.entries(arqueo).filter(([, v]) => v > 0)
      );
      await api.post(`/api/cash/close/${caja!.id}`, arqueoPayload, {
        params: {
          monto_real: totalArqueo > 0 ? totalArqueo : montoReal,
          observaciones: obs || undefined,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["caja-actual"] });
      setMontoReal(0);
      setObs("");
      setArqueo({});
    },
  });

  const totalArqueo = useMemo(
    () =>
      Object.entries(arqueo).reduce(
        (sum, [d, c]) => sum + Number(d) * Number(c || 0),
        0
      ),
    [arqueo]
  );

  const diferencia = useMemo(() => {
    if (!caja) return 0;
    const real = totalArqueo > 0 ? totalArqueo : montoReal;
    return real - caja.esperado_efectivo;
  }, [caja, totalArqueo, montoReal]);

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Caja</h1>
          <p className={ui.subtitle}>Apertura, movimientos, cierre y arqueo</p>
        </div>
      </header>

      <div className={ui.page}>
        {isLoading && (
          <div className={ui.cardPad}>
            <div className={ui.skeleton + " h-32"} />
          </div>
        )}

        {!isLoading && !caja && (
          <div className={ui.cardPad + " max-w-xl mx-auto"}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 rounded-full bg-slate-400" />
              <h2 className="text-lg font-bold text-slate-900">Caja cerrada</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              No hay caja abierta. Ingresá el monto inicial para abrir el turno.
            </p>
            <label className={ui.label}>Monto inicial</label>
            <input
              type="number"
              className={ui.input + " text-2xl font-bold"}
              value={montoInicial}
              onChange={(e) => setMontoInicial(parseFloat(e.target.value) || 0)}
              autoFocus
            />
            {abrir.isError && (
              <div className={ui.error + " mt-3"}>Error abriendo caja.</div>
            )}
            <button
              className={ui.btnPrimary + " w-full mt-4"}
              onClick={() => abrir.mutate()}
              disabled={abrir.isPending || montoInicial < 0}
            >
              {abrir.isPending ? "Abriendo..." : "Abrir caja"}
            </button>
          </div>
        )}

        {!isLoading && caja && (
          <div className="space-y-4">
            {/* Resumen + medios de pago */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className={ui.cardPad}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <h2 className="text-lg font-bold text-slate-900">
                    Caja #{caja.id}
                  </h2>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Apertura</span>
                    <span className="font-medium text-slate-900">
                      {dateTime(caja.fecha_apertura)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Monto inicial</span>
                    <span className="font-bold text-slate-900">
                      {money(caja.monto_inicial)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ventas del turno</span>
                    <span className="font-bold text-slate-900">
                      {caja.ventas_cantidad} ·{" "}
                      <span className="tabular-nums">{money(caja.ventas_total)}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className={ui.cardPad + " lg:col-span-2"}>
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
                  Medios de pago del turno
                </h3>
                {Object.keys(caja.por_medio_pago).length === 0 ? (
                  <div className="text-sm text-slate-400 py-4 text-center">
                    Sin ventas todavía
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {Object.entries(caja.por_medio_pago)
                      .sort((a, b) => b[1] - a[1])
                      .map(([medio, monto]) => {
                        const c =
                          MEDIO_COLORS[medio] ||
                          "bg-slate-50 border-slate-200 text-slate-700";
                        return (
                          <div
                            key={medio}
                            className={"rounded-xl border px-3 py-2.5 " + c}
                          >
                            <div className="text-[11px] font-bold uppercase tracking-wide opacity-80">
                              {MEDIO_LABELS[medio] || medio}
                            </div>
                            <div className="text-xl font-extrabold tabular-nums mt-0.5">
                              {money(monto)}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-3 mt-3 border-t border-slate-100">
                  <span className="text-xs uppercase tracking-wide text-slate-500 font-bold">
                    Total cobrado
                  </span>
                  <span className="text-2xl font-extrabold text-slate-900 tabular-nums">
                    {money(caja.ventas_total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Movimientos + Cierre */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <MovimientosCard caja={caja} />

              <div className={ui.cardPad}>
                <h2 className="text-lg font-bold text-slate-900 mb-3">
                  Cierre de caja
                </h2>

                <div className="bg-slate-50 rounded-xl p-3 mb-4 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Inicial</span>
                    <span className="font-bold tabular-nums">
                      {money(caja.monto_inicial)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">+ Ventas en efectivo</span>
                    <span className="font-bold tabular-nums text-emerald-600">
                      {money(caja.por_medio_pago.EFECTIVO || 0)}
                    </span>
                  </div>
                  {caja.ingresos_efectivo > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">+ Ingresos manuales</span>
                      <span className="font-bold tabular-nums text-emerald-600">
                        {money(caja.ingresos_efectivo)}
                      </span>
                    </div>
                  )}
                  {caja.egresos_efectivo > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">− Gastos / retiros</span>
                      <span className="font-bold tabular-nums text-rose-600">
                        {money(caja.egresos_efectivo)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-200">
                    <span className="font-bold text-slate-700 uppercase tracking-wide text-xs">
                      Esperado en caja
                    </span>
                    <span className="text-xl font-extrabold tabular-nums text-slate-900">
                      {money(caja.esperado_efectivo)}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className={ui.label}>
                      Monto real contado (manual)
                    </label>
                    <input
                      type="number"
                      className={ui.input}
                      value={montoReal || ""}
                      placeholder="0"
                      onChange={(e) =>
                        setMontoReal(parseFloat(e.target.value) || 0)
                      }
                    />
                  </div>

                  {(montoReal > 0 || totalArqueo > 0) && (
                    <div
                      className={
                        "rounded-xl p-3 flex justify-between items-baseline " +
                        (Math.abs(diferencia) < 0.01
                          ? "bg-emerald-50 border border-emerald-200"
                          : diferencia > 0
                            ? "bg-blue-50 border border-blue-200"
                            : "bg-rose-50 border border-rose-200")
                      }
                    >
                      <span className="text-xs uppercase tracking-wide font-bold text-slate-700">
                        Diferencia
                      </span>
                      <span
                        className={
                          "text-xl font-extrabold tabular-nums " +
                          (Math.abs(diferencia) < 0.01
                            ? "text-emerald-700"
                            : diferencia > 0
                              ? "text-blue-700"
                              : "text-rose-700")
                        }
                      >
                        {diferencia >= 0 ? "+" : ""}
                        {money(diferencia)}
                      </span>
                    </div>
                  )}

                  <div>
                    <label className={ui.label}>Observaciones</label>
                    <textarea
                      className={ui.input}
                      rows={2}
                      value={obs}
                      onChange={(e) => setObs(e.target.value)}
                    />
                  </div>

                  <details className="mt-1">
                    <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                      Arqueo por denominación
                    </summary>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      {DENOMS.map((d) => (
                        <div key={d} className="flex items-center gap-2">
                          <span className="text-sm text-slate-700 w-20">
                            {money(d)}
                          </span>
                          <span className="text-slate-400 text-sm">×</span>
                          <input
                            type="number"
                            min="0"
                            className={ui.input + " py-1"}
                            value={arqueo[d] ?? 0}
                            onChange={(e) =>
                              setArqueo((a) => ({
                                ...a,
                                [d]: parseInt(e.target.value) || 0,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-slate-200">
                      <span className="text-sm text-slate-500 uppercase tracking-wide">
                        Total arqueo
                      </span>
                      <span className="text-2xl font-bold text-slate-900 tabular-nums">
                        {money(totalArqueo)}
                      </span>
                    </div>
                  </details>

                  {cerrar.isError && (
                    <div className={ui.error}>Error cerrando caja.</div>
                  )}

                  <button
                    className={ui.btnDanger + " w-full"}
                    onClick={() => cerrar.mutate()}
                    disabled={
                      cerrar.isPending ||
                      (montoReal <= 0 && totalArqueo <= 0)
                    }
                  >
                    {cerrar.isPending ? "Cerrando..." : "Cerrar caja"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MovimientosCard({ caja }: { caja: CajaActual }) {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<"GASTO" | "INGRESO" | "RETIRO">("GASTO");
  const [concepto, setConcepto] = useState("");
  const [monto, setMonto] = useState<string>("");
  const [medio, setMedio] = useState<string>("EFECTIVO");

  const agregar = useMutation({
    mutationFn: async () => {
      await api.post(`/api/cash/${caja.id}/movimiento`, {
        tipo,
        concepto,
        monto: parseFloat(monto) || 0,
        medio_pago: medio,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["caja-actual"] });
      setConcepto("");
      setMonto("");
    },
  });

  const borrar = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/cash/${caja.id}/movimiento/${id}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["caja-actual"] }),
  });

  const canAdd =
    concepto.trim().length > 0 && parseFloat(monto) > 0;

  return (
    <div className={ui.cardPad}>
      <h2 className="text-lg font-bold text-slate-900 mb-3">
        Movimientos del turno
      </h2>

      <div className="bg-slate-50 rounded-xl p-3 mb-3 space-y-2">
        <div className="flex gap-1.5">
          {(
            [
              { id: "GASTO", label: "Gasto", color: "rose" },
              { id: "RETIRO", label: "Retiro", color: "rose" },
              { id: "INGRESO", label: "Ingreso", color: "emerald" },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTipo(t.id)}
              className={
                "flex-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors " +
                (tipo === t.id
                  ? t.color === "emerald"
                    ? "bg-emerald-500 text-white"
                    : "bg-rose-500 text-white"
                  : "bg-white border border-slate-200 text-slate-700")
              }
            >
              {t.label}
            </button>
          ))}
        </div>
        <input
          className={ui.input}
          placeholder="Concepto (ej: cambio, kiosko proveedor, pago empleado)"
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            className={ui.input}
            placeholder="Monto"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
          />
          <select
            className={ui.input}
            value={medio}
            onChange={(e) => setMedio(e.target.value)}
          >
            <option value="EFECTIVO">Efectivo</option>
            <option value="DEBITO">Débito</option>
            <option value="CREDITO">Crédito</option>
            <option value="TRANSFERENCIA">Transferencia</option>
            <option value="QR">MP QR</option>
            <option value="OTRO">Otro</option>
          </select>
        </div>
        <button
          className={ui.btnPrimary + " w-full"}
          disabled={!canAdd || agregar.isPending}
          onClick={() => agregar.mutate()}
        >
          {agregar.isPending ? "Agregando..." : `Agregar ${tipo.toLowerCase()}`}
        </button>
      </div>

      {caja.movimientos.length === 0 ? (
        <div className="text-sm text-slate-400 py-4 text-center">
          Sin gastos ni movimientos manuales todavía
        </div>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-auto">
          {caja.movimientos.map((m) => {
            const negativo =
              m.tipo === "EGRESO" || m.tipo === "GASTO" || m.tipo === "RETIRO";
            return (
              <div
                key={m.id}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-100 hover:border-slate-200"
              >
                <span
                  className={
                    "text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md " +
                    (negativo
                      ? "bg-rose-100 text-rose-700"
                      : "bg-emerald-100 text-emerald-700")
                  }
                >
                  {m.tipo}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {m.concepto}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    {new Date(m.fecha).toLocaleTimeString("es-AR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    · {MEDIO_LABELS[m.medio_pago] || m.medio_pago}
                  </div>
                </div>
                <span
                  className={
                    "font-extrabold tabular-nums text-sm " +
                    (negativo ? "text-rose-600" : "text-emerald-600")
                  }
                >
                  {negativo ? "−" : "+"}
                  {money(m.monto)}
                </span>
                <button
                  onClick={() => borrar.mutate(m.id)}
                  className="text-slate-400 hover:text-rose-600 text-xs"
                  title="Borrar movimiento"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
